import React from 'react';
import {
  Table, TableHead, TableBody, TableRow, TableCell, TextField, IconButton, Button, Tooltip, InputAdornment, Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { SlabForm, addSlab, fromKmOf, removeSlab, updateSlab } from './rateCardForm';

interface Props {
  slabs: SlabForm[];
  onChange: (slabs: SlabForm[]) => void;
  disabled?: boolean;
}

/** Distance slabs: "from" is always the previous row's "to", so ranges stay contiguous from 0 km. */
const SlabTable: React.FC<Props> = ({ slabs, onChange, disabled }) => (
  <>
    <Table size="small" sx={{ '& td, & th': { px: 1 } }}>
      <TableHead>
        <TableRow>
          <TableCell sx={{ fontWeight: 700, width: 110 }}>From km</TableCell>
          <TableCell sx={{ fontWeight: 700, width: 150 }}>To km</TableCell>
          <TableCell sx={{ fontWeight: 700 }}>₹ per km</TableCell>
          <TableCell sx={{ width: 48 }} />
        </TableRow>
      </TableHead>
      <TableBody>
        {slabs.map((s, i) => {
          const last = i === slabs.length - 1;
          const from = fromKmOf(slabs, i);
          return (
            <TableRow key={i}>
              <TableCell><Typography variant="body2">{Number.isFinite(from) ? from : '—'}</Typography></TableCell>
              <TableCell>
                {last ? (
                  <Typography variant="body2" color="text.secondary">and above</Typography>
                ) : (
                  <TextField
                    size="small" type="number" value={s.toKm} disabled={disabled}
                    inputProps={{ step: '0.5', min: 0, 'aria-label': `Slab ${i + 1} to km` }}
                    onChange={(e) => onChange(updateSlab(slabs, i, { toKm: e.target.value }))}
                  />
                )}
              </TableCell>
              <TableCell>
                <TextField
                  size="small" type="number" value={s.perKm} disabled={disabled}
                  inputProps={{ step: '0.1', min: 0, 'aria-label': `Slab ${i + 1} rupees per km` }}
                  InputProps={{ startAdornment: <InputAdornment position="start">₹</InputAdornment> }}
                  onChange={(e) => onChange(updateSlab(slabs, i, { perKm: e.target.value }))}
                />
              </TableCell>
              <TableCell>
                {slabs.length > 1 && (
                  <Tooltip title="Remove slab (its neighbour takes over the range)">
                    <span>
                      <IconButton size="small" disabled={disabled} onClick={() => onChange(removeSlab(slabs, i))} aria-label={`Remove slab ${i + 1}`}>
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                )}
              </TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
    <Button size="small" startIcon={<AddIcon />} disabled={disabled || slabs.length >= 20} onClick={() => onChange(addSlab(slabs))} sx={{ mt: 0.5 }}>
      Add slab
    </Button>
  </>
);

export default SlabTable;
